import React from "react";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import {
    Close,
    HeadsetRounded,
    InfoRounded,
    KeyboardArrowDownRounded,
} from "@mui/icons-material";
import {
    MediaControlBar,
    MediaFullscreenButton,
    MediaMuteButton,
    MediaPlayButton,
    MediaSeekBackwardButton,
    MediaSeekForwardButton,
    MediaTimeDisplay,
    MediaTimeRange,
} from "media-chrome/react";

const MaximizedUI = ({
    watchType,
    showEpisodes,
    setShowEpisodes,
    handleMinimize,
    handleClosePlayer,
    dispatch,
    setWatchType,
    setIsMuted,
    setVolume,
    playerRef,
    isMobile,
}) => {
    const theme = useTheme();

    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    zIndex: 10,
                }}
            >
                <Tooltip title={"Passer en mode écoute"} placement="bottom">
                    <IconButton
                        onClick={() => {
                            dispatch(setWatchType("listen"));
                        }}
                        sx={{
                            color: "white",
                            backgroundColor: (theme) => theme.palette.videoColors.button,
                            "&:hover": {
                                backgroundColor: (theme) =>
                                    theme.palette.videoColors.buttonHover,
                            },
                        }}
                    >
                        <HeadsetRounded />
                    </IconButton>
                </Tooltip>
                <Tooltip title={"Voir la liste des épisodes"} placement="bottom">
                    <IconButton
                        onClick={() => {
                            setShowEpisodes(!showEpisodes);
                        }}
                        sx={{
                            color: "white",
                            backgroundColor: (theme) => theme.palette.videoColors.button,
                            "&:hover": {
                                backgroundColor: (theme) =>
                                    theme.palette.videoColors.buttonHover,
                            },
                        }}
                    >
                        <InfoRounded color={showEpisodes ? "primary" : undefined} />
                    </IconButton>
                </Tooltip>
                <Tooltip title={"Minimiser la vidéo"} placement="bottom">
                    <IconButton
                        onClick={handleMinimize}
                        sx={{
                            color: "white",
                            backgroundColor: (theme) => theme.palette.videoColors.button,
                            "&:hover": {
                                backgroundColor: (theme) =>
                                    theme.palette.videoColors.buttonHover,
                            },
                        }}
                    >
                        <KeyboardArrowDownRounded />
                    </IconButton>
                </Tooltip>
                <Tooltip title={"Fermer"} placement="bottom">
                    <IconButton
                        onClick={handleClosePlayer}
                        sx={{
                            color: "white",
                            backgroundColor: (theme) => theme.palette.videoColors.button,
                            "&:hover": {
                                backgroundColor: (theme) =>
                                    theme.palette.videoColors.buttonHover,
                            },
                        }}
                    >
                        <Close />
                    </IconButton>
                </Tooltip>
            </Box>

            <MediaControlBar
                style={{
                    width: "100%",
                    minWidth: "100%",
                    display: "flex",
                    flexDirection: "row",
                    opacity: 1,
                    position: "absolute",
                    bottom: "44px", // Above the main control bar
                }}
            >
                <MediaTimeRange />
                <MediaTimeDisplay showDuration />
            </MediaControlBar>
            <MediaControlBar
                style={{
                    width: "100%",
                    minWidth: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    opacity: 1,
                    backgroundColor: theme.palette.videoColors.button,
                    position: "absolute",
                    bottom: 0,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "row",
                    }}
                >
                    <MediaPlayButton />
                    <MediaSeekBackwardButton seekOffset={5} />
                    <MediaSeekForwardButton seekOffset={5} />
                    <MediaMuteButton
                        onClick={() => {
                            setIsMuted((prev) => !prev);
                        }}
                    />
                    {!isMobile && (
                        <CustomVolumeSlider
                            playerRef={playerRef}
                            onVolumeChanged={setVolume}
                        />
                    )}
                </Box>
                {watchType === "watch" && <MediaFullscreenButton />}
            </MediaControlBar>
        </>
    );
};

export default MaximizedUI;

const CustomVolumeSlider = ({ initialVolume = 1, onVolumeChanged }) => {
    const theme = useTheme();
    const [volume, setVolume] = React.useState(initialVolume);

    const handleVolumeChange = React.useCallback((event) => {
        const newVolume = parseFloat(event.target.value);
        setVolume(newVolume);
        onVolumeChanged?.(newVolume);
    }, [onVolumeChanged]);

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px",
                background: theme.palette.videoColors.button,
            }}
        >
            <input
                className="volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                style={{
                    width: "100px",
                    height: "4px",
                    color: "white",
                    cursor: "pointer"
                }}
                aria-label="Video Volume Slider"
            />
        </div>
    );
};
